import Image from 'next/image';
import { PageData } from '@interfaces';
import styles from './PageHeader.module.css';

interface PageHeaderProps {
  meta: PageData;
  baseUrl: string;
}

const customLoader = ({ src, width, quality }: { src: string; width: number; quality?: number }) => {
  return `${src}?w=${width}&q=${quality || 75}`;
};

const normalizeUrl = (base: string, path: string | undefined): string => {
  if (!path) return base;
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`.replace(/([^:])\/+/g, '$1/');
};

// Make our own absolute URLs origin-relative so they load from the current host
// (dev or prod) instead of a hard-coded domain. External URLs are left alone.
const ownHostRelative = (u?: string): string =>
  (u || '').replace(/^https?:\/\/(www\.)?thedailydialectics\.com/i, '');

/**
 * The primary-image convention.
 *
 * variables.json declares `thumbnail` as the image's DIRECTORY under the public
 * root (e.g. "imgs/mems/fluid"). That directory holds:
 *   • info.json   — the image's metadata (loaded into meta.imageData)
 *   • index.html  — the image's public info page
 *   • the file    — the actual image asset
 *
 * From that, every page resolves the image the same way:
 *   src   → the canonical file URL    (info.json schema.url / social og:image)
 *   href  → the directory info page    (BASE_URL/thumbnail/  → index.html)
 *   alt   → thumbnail_alt | info.json.alt
 *   caption → thumbnail_caption | info.json.caption
 *   schema → info.json.schema (emitted as JSON-LD next to the image)
 *
 * The href is *meant* to point at the directory — that is the info page. Only
 * the <img src> needs the file, since the directory serves HTML.
 */
function resolvePrimaryImage(meta: PageData, baseUrl: string) {
  const img = meta.imageData;
  const thumbnailDir = (meta.thumbnail as string) || '';

  // src — the actual image file. Never the bare directory (that serves HTML).
  const ext = (img?.ext || '').replace(/^\./, '');
  const fileFromDir =
    thumbnailDir && img?.filename && ext
      ? normalizeUrl(baseUrl, `${thumbnailDir}/${img.filename}.${ext}`)
      : '';
  const src =
    img?.schema?.url ||
    img?.social_meta?.['og:image'] ||
    fileFromDir;

  // href — the image's info page: the thumbnail directory's index.html.
  const infoPageUrl =
    img?.page_url ||
    (thumbnailDir ? `${normalizeUrl(baseUrl, thumbnailDir)}/` : src);

  return {
    src: ownHostRelative(src),
    infoPageUrl: ownHostRelative(infoPageUrl),
    alt: meta.thumbnail_alt || img?.alt || `Illustration for ${meta.title}`,
    caption: meta.thumbnail_caption || img?.caption || '',
    width: img?.dimensions?.width || 800,
    height: img?.dimensions?.height || 600,
    schema: img?.schema ?? null,
  };
}

import KeywordChips from './KeywordChips';

export default function PageHeader({ meta, baseUrl }: PageHeaderProps) {
  const { src, infoPageUrl, alt, caption, width, height, schema } =
    resolvePrimaryImage(meta, baseUrl);

  const keywords = (meta.keywords_str || '')
    .split(/[,|]/)
    .map(k => k.trim())
    .filter(Boolean);

  return (
    <header className={styles.pageHeader}>

      {/* Title links to the image's info page, not the raw asset */}
      <h1 className={styles.title}>
        {infoPageUrl ? (
          <a href={infoPageUrl} rel="noopener noreferrer">
            {meta.title}
          </a>
        ) : (
          meta.title
        )}
      </h1>

      {src && (
        <figure
          className={styles.imageContainer}
          itemScope
          itemType="https://schema.org/ImageObject"
        >
          {/* Image links to its info page (the thumbnail directory) */}
          <a href={infoPageUrl} rel="noopener noreferrer">
            <Image
              loader={customLoader}
              src={src}
              width={width}
              height={height}
              alt={alt}
              title={meta.imageData?.title || alt}
              priority
              className={styles.headerImage}
              itemProp="contentUrl"
            />
          </a>
          {caption && (
            <figcaption className={styles.imageCaption} itemProp="caption">
              {caption}
            </figcaption>
          )}
          {schema && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            />
          )}
        </figure>
      )}

      <section className={styles.synopsis}>
        <h2>Synopsis</h2>
        <p>{meta.description}</p>
        {keywords.length > 0 && (
          <KeywordChips
            keywords={keywords}
            className={styles.keywordsList}
            chipClassName={styles.keywordChip}
          />
        )}
      </section>

    </header>
  );
}